import React, { useState, useEffect } from "react";
import { Button, Form, Row, Col, Modal, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaLock,
  FaCheck,
} from "react-icons/fa";
import { MdDescription } from "react-icons/md";
import { MdAnalytics } from "react-icons/md";
import { RiShieldCheckFill } from "react-icons/ri";
import logo from "../../assets/images/Enterprise Logo-01 transparent 1.png";
import styles from "./GuestInquiry.module.scss";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { error } from "console";
import AlertModal from "../../components/alertModal/alertModal";
import { BASE_URL } from "../../const/URL";
import TncPdf from "../../assets/pdf/tncGest.pdf";

const GuestInquiry = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    mobileNumber: "",
    email: "",
    interestProduct: "",
    requirement: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpReference, setOtpReference] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [consent, setConsent] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [referenceNumber, setReference] = useState("");
  const [isShowTnC, setIsShowTnc] = useState<boolean>(false);

  const initTnCModal = () => {
    return setIsShowTnc(!isShowTnC);
  };

  // Validate email format
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Validate mobile number format
  const validateMobile = (mobile: string) => {
    const re = /^[0-9]{10}$/;
    return re.test(mobile);
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = "Contact person is required";
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!validateMobile(formData.mobileNumber)) {
      newErrors.mobileNumber = "Please enter a valid 10-digit mobile number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.interestProduct) {
      newErrors.interestProduct = "Please select an interest product";
    }

    if (!formData.requirement.trim()) {
      newErrors.requirement = "Requirement is required";
    }

    if (!consent) {
      newErrors.consent = "You must agree to the consent";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value.slice(0, 1);
    setOtp(updatedOtp);
    setOtpError("");

    if (value && index < otp.length - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleVerify = async () => {
    if (!validateMobile(formData.mobileNumber)) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: "Please enter a valid 10-digit mobile number",
      }));
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/guest-otp/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ mobileNumber: formData.mobileNumber }),
      });

      const data = await response.json();

      if (response.ok && data.isSuccess) {
        setOtpReference(data.dataBundle);
        setShowOtpModal(true);
        setOtpSent(true);
      } else {
        setErrors((prev) => ({
          ...prev,
          mobileNumber: data.errorMessage || "Error sending OTP",
        }));
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: "An error occurred while sending OTP",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/guest-otp/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          otpReference: otpReference,
          otp: otpString,
        }),
      });

      const data = await response.json();

      if (response.ok && data.isSuccess) {
        setOtpVerified(true);
        setShowOtpModal(false);
      } else {
        setOtpError(data.errorMessage || "OTP verification failed");
      }
    } catch (error) {
      setOtpError("An error occurred while verifying OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!otpVerified) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: "Please verify your mobile number with OTP",
      }));
      return;
    }

    setIsLoading(true);
    try {
      const leadData = {
        COMPANYNAME: formData.companyName,
        CONTACTNAME: formData.contactPerson,
        CONTACTNO: formData.mobileNumber,
        EMAIL: formData.email,
        INTERSTEDPRODUCT: formData.interestProduct,
        REMARKS: `Catogery:${formData.interestProduct},${formData.requirement}`,
      };

      const response = await fetch(
        `${BASE_URL}/api/v1/ENTBitrix24/api/Bitrix/AddLeads`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(leadData),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setShowSuccess(true);
        setReference(responseData.referenceNo || "N/A");
      } else {
        const data = await response.json();
        setErrors((prev) => ({
          ...prev,
          form: data.errorMessage || "Error submitting lead",
        }));
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        form: "An error occurred while submitting the lead",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Auto focus first empty OTP field when modal opens
  useEffect(() => {
    if (showOtpModal) {
      const firstEmptyIndex = otp.findIndex((num) => num === "");
      const inputId = `otp-${firstEmptyIndex !== -1 ? firstEmptyIndex : 0}`;
      setTimeout(() => {
        const input = document.getElementById(inputId);
        input?.focus();
      }, 100);
    }
  }, [showOtpModal, otp]);

  return (
    <div className={styles.guestInquiryContainer}>
      <div className={styles.formWrapper}>
        {/* Left Side - Logo and Info */}
        <div className={styles.formSidebar}>
          <div className={styles.sidebarContent}>
            <img src={logo} alt="SLT-MOBITEL Logo" className={styles.logo} />
            <h2>Enterprise & SME Solutions</h2>
            <p className={styles.tagline}>
              Submit your requirement in brief to get in touch with our support
              team.
            </p>
            <div className={styles.features}>
              <div className={styles.featureItem}>
                <RiShieldCheckFill className={styles.featureIcon} />
                <span>Secure & Confidential</span>
              </div>
              <div className={styles.featureItem}>
                <FaCheck className={styles.featureIcon} />
                <span>Quick Response</span>
              </div>
              <div className={styles.featureItem}>
                <FaCheck className={styles.featureIcon} />
                <span>Expert Consultation</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className={styles.formContent}>
          <AlertModal
            show={showSuccess}
            variant="success"
            buttons={[
              {
                text: "Ok",
                onClick: () => {
                  navigate("/login");
                  setShowSuccess(false);
                },
                variant: "success",
              },
            ]}
          >
            <label>
              Your inquiry has been submitted successfully. Your referenceNo is:{" "}
              {referenceNumber}
            </label>
          </AlertModal>

          {errors.form && (
            <Alert variant="danger" className="mb-4">
              {errors.form}
            </Alert>
          )}

          <Form onSubmit={handleSubmit} noValidate>
            <h3 className={styles.formTitle}>Business Inquiry Form</h3>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaBuilding className={styles.inputIcon} /> Company Name
              </Form.Label>
              <Form.Control
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                isInvalid={!!errors.companyName}
                placeholder="Enter your company name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.companyName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaUser className={styles.inputIcon} /> Contact Person Name
              </Form.Label>
              <Form.Control
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                isInvalid={!!errors.contactPerson}
                placeholder="Enter contact person name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.contactPerson}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaPhone className={styles.inputIcon} /> Contact No
              </Form.Label>
              <div className="d-flex align-items-start">
                <div className="flex-grow-1">
                  <Form.Control
                    type="text"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    isInvalid={!!errors.mobileNumber}
                    placeholder="Enter 10-digit mobile number"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.mobileNumber}
                  </Form.Control.Feedback>
                </div>
                <Button
                  variant={otpVerified ? "success" : "primary"}
                  className={`ms-2 ${styles.verifyBtn}`}
                  onClick={handleVerify}
                  disabled={
                    otpVerified ||
                    isLoading ||
                    !validateMobile(formData.mobileNumber)
                  }
                >
                  {otpVerified ? (
                    <>
                      <FaCheck className="me-1" /> Verified
                    </>
                  ) : isLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Verify"
                  )}
                </Button>
              </div>
              {otpVerified && (
                <small className="text-success d-block mt-1">
                  Mobile number verified successfully
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaEnvelope className={styles.inputIcon} /> E-mail
              </Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
                placeholder="Enter your email address"
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <MdAnalytics className={styles.inputIcon} /> Interest Product
              </Form.Label>
              <Form.Select
                name="interestProduct"
                value={formData.interestProduct}
                onChange={handleChange}
                isInvalid={!!errors.interestProduct}
              >
                <option value="">Select an option</option>
                <option value="Networking">Networking</option>
                <option value="Akaza Multi Cloud">Akaza Multi Cloud</option>
                <option value="Digital Services">Digital Services</option>
                <option value="Data Center">Data Center</option>
                <option value="Internet">Internet</option>
                <option value="Cyber Security">Cyber Security</option>
                <option value="Voice & Collaboration">
                  Voice & Collaboration
                </option>
                <option value="Akaza Arcadia">Akaza Arcadia</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.interestProduct}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <MdDescription className={styles.inputIcon} /> Requirement in
                  brief
                  <div style={{ marginLeft: "5px" }}>
                    <a
                      href="https://slt.lk/en/business"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.productLink}
                    >
                      ( For more product details, click here )
                    </a>
                  </div>
                </Box>
              </Form.Label>
              <Form.Control
                as="textarea"
                name="requirement"
                maxLength={250}
                value={formData.requirement}
                onChange={handleChange}
                rows={4}
                style={{ resize: "none" }}
                isInvalid={!!errors.requirement}
                placeholder="Describe your requirement (max 250 characters)"
              />
              <Form.Control.Feedback type="invalid">
                {errors.requirement}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                id="consent-checkbox"
                label={
                  <span>
                    By clicking the submission button,{" "}
                    <a
                      onClick={() => setIsShowTnc(true)}
                      className={styles.consentLink}
                    >
                      I agree to Consent
                    </a>
                  </span>
                }
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                isInvalid={!!errors.consent}
              />
              {errors.consent && (
                <div className="text-danger small mt-1">{errors.consent}</div>
              )}
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              className={styles.submitBtn}
              disabled={isLoading || !otpVerified || !consent}
            >
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                "SUBMIT INQUIRY"
              )}
            </Button>
          </Form>
        </div>
      </div>

      {/* Beautiful OTP Modal */}
      <Modal
        show={showOtpModal}
        onHide={() => setShowOtpModal(false)}
        centered
        backdrop="static"
        className={styles.otpModal}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="w-100 text-center">
            <div className={styles.otpIconContainer}>
              <FaLock className={styles.otpIcon} />
            </div>
            <h4 className="mt-3">Mobile Verification</h4>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center pt-0">
          <p className="mb-4">
            We've sent a 6-digit verification code to
            <br />
            <strong>{formData.mobileNumber}</strong>
          </p>

          <div className={styles.otpInputs}>
            {otp.map((value, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                value={value}
                onChange={(e) => handleOtpChange(e.target.value, index)}
                className={`${styles.otpInput} ${value ? styles.hasValue : ""}`}
                maxLength={1}
                autoComplete="off"
              />
            ))}
          </div>

          {otpError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {otpError}
            </Alert>
          )}

          <Button
            variant="primary"
            onClick={handleOtpVerify}
            className={`${styles.verifyOtpBtn} mt-4`}
            disabled={isLoading || otp.join("").length !== 6}
          >
            {isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Verify OTP"
            )}
          </Button>

          <div className="mt-3">
            <small className="text-muted">
              Didn't receive code?{" "}
              <a href="#" className={styles.resendLink}>
                Resend
              </a>
            </small>
          </div>
        </Modal.Body>
      </Modal>

      <Dialog
        open={isShowTnC}
        onClose={initTnCModal}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialogTitle-root": {
            backgroundColor: "#1e3a8a",
            color: "white",
            fontWeight: "600",
            fontSize: "1.5rem",
            padding: "20px 30px",
          },
          "& .MuiDialogContent-root": {
            padding: "30px",
            backgroundColor: "#f9fafb",
          },
          "& .MuiDialogActions-root": {
            padding: "16px 30px",
            justifyContent: "flex-end",
          },
        }}
      >
        <DialogTitle>Terms and Conditions</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <iframe
              title="Terms and Conditions"
              width="100%"
              height="100%"
              src={TncPdf}
              frameBorder="0"
              style={{
                border: "none",
                borderRadius: "20px",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={initTnCModal}
            style={{
              fontWeight: "bold",
              padding: "10px 20px",
              textTransform: "none",
              color: "white",
              backgroundColor: "#27548A",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GuestInquiry;
